import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { BBQLayout } from "./layouts";
import { KioskProvider } from "./contexts/KioskContext";
import ScrollToTop from "./components/ScrollToTop";

// Critical path - load immediately
import BBQHomePage from "./pages/bbq/BBQHomePage";

// Lazy load all other routes for code splitting
const BBQCategoryPage = lazy(() => import("./pages/bbq/BBQCategoryPage"));
const BBQSubcategoryPage = lazy(() => import("./pages/bbq/BBQSubcategoryPage"));
const BBQShowroomPage = lazy(() => import("./pages/bbq/BBQShowroomPage"));
const BBQQuotePage = lazy(() => import("./pages/bbq/BBQQuotePage"));
const BBQFinancingPage = lazy(() => import("./pages/bbq/BBQFinancingPage"));
const BBQDeliveryPage = lazy(() => import("./pages/bbq/BBQDeliveryPage"));
const BBQWarrantyPage = lazy(() => import("./pages/bbq/BBQWarrantyPage"));
const BBQDesignServicesPage = lazy(() => import("./pages/bbq/BBQDesignServicesPage"));
const BBQClearancePage = lazy(() => import("./pages/bbq/BBQClearancePage"));
const BBQSchedulePage = lazy(() => import("./pages/bbq/BBQSchedulePage"));
const BBQGuidesPage = lazy(() => import("./pages/bbq/BBQGuidesPage"));
const BBQBlogPage = lazy(() => import("./pages/bbq/BBQBlogPage"));
const BBQBlogCategoryPage = lazy(() => import("./pages/bbq/BBQBlogCategoryPage"));
const BBQBlogPostPage = lazy(() => import("./pages/bbq/BBQBlogPostPage"));
const BBQVideosPage = lazy(() => import("./pages/bbq/BBQVideosPage"));
const BBQVideoDetailPage = lazy(() => import("./pages/bbq/BBQVideoDetailPage"));
const BBQBuyingGuidePage = lazy(() => import("./pages/bbq/BBQBuyingGuidePage"));
const BBQPrivacyPage = lazy(() => import("./pages/bbq/BBQPrivacyPage"));
const BBQTermsPage = lazy(() => import("./pages/bbq/BBQTermsPage"));
const BBQTradePage = lazy(() => import("./pages/bbq/BBQTradePage"));
const BBQCommercialPage = lazy(() => import("./pages/bbq/BBQCommercialPage"));
const BBQAccessoriesPage = lazy(() => import("./pages/bbq/BBQAccessoriesPage"));
const BBQCityPage = lazy(() => import("./pages/bbq/BBQCityPage"));
const BBQBrandHubPage = lazy(() => import("./pages/bbq/BBQBrandHubPage"));
const BBQBrandPage = lazy(() => import("./pages/bbq/BBQBrandPage"));
const BBQComparisonPage = lazy(() => import("./pages/bbq/BBQComparisonPage"));
const BBQBestForPage = lazy(() => import("./pages/bbq/BBQBestForPage"));
const BBQLocationHubPage = lazy(() => import("./pages/bbq/BBQLocationHubPage"));
const BBQLocationsPage = lazy(() => import("./pages/bbq/BBQLocationsPage"));
const BBQOutdoorKitchenCityPage = lazy(() => import("./pages/bbq/BBQOutdoorKitchenCityPage"));
const BBQOrderTrackerPage = lazy(() => import("./pages/bbq/BBQOrderTrackerPage"));
const BBQServiceAreasPage = lazy(() => import("./pages/bbq/BBQServiceAreasPage"));
const BBQCityLandingPage = lazy(() => import("./pages/bbq/BBQCityLandingPage"));
const BBQNeighborhoodPage = lazy(() => import("./pages/bbq/BBQNeighborhoodPage"));
const BBQDesignWizardPage = lazy(() => import("./pages/bbq/BBQDesignWizardPage"));
const BBQKarmaPizzaOvensPage = lazy(() => import("./pages/bbq/BBQKarmaPizzaOvensPage"));
const BBQPizzaOvenGuidePage = lazy(() => import("./pages/bbq/BBQPizzaOvenGuidePage"));
const BBQPizzaAccessoriesPage = lazy(() => import("./pages/bbq/BBQPizzaAccessoriesPage"));
const BBQPizzaRecipesPage = lazy(() => import("./pages/bbq/BBQPizzaRecipesPage"));
const BBQKioskPage = lazy(() => import("./pages/bbq/BBQKioskPage"));
const BBQInstallPage = lazy(() => import("./pages/bbq/BBQInstallPage"));
const CustomerContractPage = lazy(() => import("./pages/bbq/CustomerContractPage"));

// Admin pages - lazy load
const LeadsDashboard = lazy(() => import("./pages/admin/LeadsDashboard"));
const TradeLeadsDashboard = lazy(() => import("./pages/admin/TradeLeadsDashboard"));
const AppointmentsDashboard = lazy(() => import("./pages/admin/AppointmentsDashboard"));
const OrdersDashboard = lazy(() => import("./pages/admin/OrdersDashboard"));
const AdminAuthPage = lazy(() => import("./pages/admin/AdminAuthPage"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const VideosAdmin = lazy(() => import("./pages/admin/VideosAdmin"));
const SwatchesAdmin = lazy(() => import("./pages/admin/SwatchesAdmin"));
const BrandLogosAdmin = lazy(() => import("./pages/admin/BrandLogosAdmin"));
const CommissionDashboard = lazy(() => import("./pages/admin/CommissionDashboard"));
const TimeclockDashboard = lazy(() => import("./pages/admin/TimeclockDashboard"));
const SMSDashboard = lazy(() => import("./pages/admin/SMSDashboard"));
const ThumbnailGeneratorPage = lazy(() => import("./pages/admin/ThumbnailGeneratorPage"));
const WizardLeadsDashboard = lazy(() => import("./pages/admin/WizardLeadsDashboard"));
const GLBAssetsAdmin = lazy(() => import("./pages/admin/GLBAssetsAdmin"));

// Worker portal - lazy load
const WorkerLoginPage = lazy(() => import("./pages/worker/WorkerLoginPage").then(m => ({ default: m.WorkerLoginPage })));
const WorkerOnboardingPage = lazy(() => import("./pages/worker/WorkerOnboardingPage").then(m => ({ default: m.WorkerOnboardingPage })));
const WorkerClockPage = lazy(() => import("./pages/worker/WorkerClockPage").then(m => ({ default: m.WorkerClockPage })));
const WorkerTimesheetPage = lazy(() => import("./pages/worker/WorkerTimesheetPage").then(m => ({ default: m.WorkerTimesheetPage })));
const WorkerProfilePage = lazy(() => import("./pages/worker/WorkerProfilePage").then(m => ({ default: m.WorkerProfilePage })));

// PPC pages - lazy load
const PPCTexasHubPage = lazy(() => import("./pages/ppc/PPCTexasHubPage"));
const PPCCityPage = lazy(() => import("./pages/ppc/PPCCityPage"));
const PPCCaliforniaHubPage = lazy(() => import("./pages/ppc/PPCCaliforniaHubPage"));
const PPCCACityPage = lazy(() => import("./pages/ppc/PPCCACityPage"));

// California pages - lazy load
const CAShowroomPage = lazy(() => import("./pages/california/CAShowroomPage"));
const CAInlandEmpirePage = lazy(() => import("./pages/california/CAInlandEmpirePage"));
const CALosAngelesPage = lazy(() => import("./pages/california/CALosAngelesPage"));
const CASchedulePage = lazy(() => import("./pages/california/CASchedulePage"));

// Other components - lazy load
const NotFound = lazy(() => import("./pages/NotFound"));
const BlogRedirect = lazy(() => import("./components/BlogRedirect").then(m => ({ default: m.BlogRedirect })));
const WorkerAuthProvider = lazy(() => import("./contexts/WorkerAuthContext").then(m => ({ default: m.WorkerAuthProvider })));
const QuoteButtonLinter = lazy(() => import("./components/dev/QuoteButtonLinter").then(m => ({ default: m.QuoteButtonLinter })));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Helper component to wrap routes with BBQLayout for Quote Modal
const WithBBQLayout = ({ children }: { children: React.ReactNode }) => (
  <BBQLayout>{children}</BBQLayout>
);

// Wrapper for lazy components with Suspense
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// Wrapper for worker routes with auth
const WorkerRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <WorkerAuthProvider>{children}</WorkerAuthProvider>
  </Suspense>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <KioskProvider>
            <ScrollToTop />
            <Routes>
              {/* Homepage - BBQ (not lazy loaded for best LCP) */}
              <Route path="/" element={<WithBBQLayout><BBQHomePage /></WithBBQLayout>} />
              
              {/* Redirect old /bbq path to root */}
              <Route path="/bbq" element={<Navigate to="/" replace />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<Navigate to="/admin/auth" replace />} />
              <Route path="/admin/auth" element={<LazyRoute><AdminAuthPage /></LazyRoute>} />
              <Route path="/admin/leads" element={<LazyRoute><LeadsDashboard /></LazyRoute>} />
              <Route path="/admin/trade-leads" element={<LazyRoute><TradeLeadsDashboard /></LazyRoute>} />
              <Route path="/admin/appointments" element={<LazyRoute><AppointmentsDashboard /></LazyRoute>} />
              <Route path="/admin/orders" element={<LazyRoute><OrdersDashboard /></LazyRoute>} />
              <Route path="/admin/users" element={<LazyRoute><UserManagement /></LazyRoute>} />
              <Route path="/admin/videos" element={<LazyRoute><VideosAdmin /></LazyRoute>} />
              <Route path="/admin/swatches" element={<LazyRoute><SwatchesAdmin /></LazyRoute>} />
              <Route path="/admin/brand-logos" element={<LazyRoute><BrandLogosAdmin /></LazyRoute>} />
              <Route path="/admin/commissions" element={<LazyRoute><CommissionDashboard /></LazyRoute>} />
              <Route path="/admin/timeclock" element={<LazyRoute><TimeclockDashboard /></LazyRoute>} />
              <Route path="/admin/sms" element={<LazyRoute><SMSDashboard /></LazyRoute>} />
              <Route path="/admin/thumbnails" element={<LazyRoute><ThumbnailGeneratorPage /></LazyRoute>} />
              <Route path="/admin/wizard-leads" element={<LazyRoute><WizardLeadsDashboard /></LazyRoute>} />
              <Route path="/admin/glb-assets" element={<LazyRoute><GLBAssetsAdmin /></LazyRoute>} />
              
              {/* Worker Portal Routes */}
              <Route path="/worker-portal" element={<WorkerRoute><WorkerLoginPage /></WorkerRoute>} />
              <Route path="/worker-portal/onboarding" element={<WorkerRoute><WorkerOnboardingPage /></WorkerRoute>} />
              <Route path="/worker-portal/clock" element={<WorkerRoute><WorkerClockPage /></WorkerRoute>} />
              <Route path="/worker-portal/timesheet" element={<WorkerRoute><WorkerTimesheetPage /></WorkerRoute>} />
              <Route path="/worker-portal/profile" element={<WorkerRoute><WorkerProfilePage /></WorkerRoute>} />
              
              {/* Kiosk Routes - standalone flow that returns to kiosk after submission */}
              <Route path="/kiosk" element={<LazyRoute><BBQKioskPage /></LazyRoute>} />
              <Route path="/kiosk/design" element={<LazyRoute><BBQDesignWizardPage /></LazyRoute>} />
              
              {/* BBQ Routes - wrapped with BBQLayout for Quote Modal */}
              <Route path="/order-tracker" element={<WithBBQLayout><LazyRoute><BBQOrderTrackerPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/showroom" element={<WithBBQLayout><LazyRoute><BBQShowroomPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/quote" element={<WithBBQLayout><LazyRoute><BBQQuotePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/schedule" element={<WithBBQLayout><LazyRoute><BBQSchedulePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/financing" element={<WithBBQLayout><LazyRoute><BBQFinancingPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/delivery" element={<WithBBQLayout><LazyRoute><BBQDeliveryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/warranty" element={<WithBBQLayout><LazyRoute><BBQWarrantyPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/design-services" element={<WithBBQLayout><LazyRoute><BBQDesignServicesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/clearance" element={<WithBBQLayout><LazyRoute><BBQClearancePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/accessories" element={<WithBBQLayout><LazyRoute><BBQAccessoriesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/guides" element={<WithBBQLayout><LazyRoute><BBQGuidesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/guides/:category" element={<WithBBQLayout><LazyRoute><BBQBlogCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/guides/:category/:slug" element={<WithBBQLayout><LazyRoute><BBQBlogPostPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/buying-guide" element={<WithBBQLayout><LazyRoute><BBQBuyingGuidePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/blog" element={<WithBBQLayout><LazyRoute><BBQBlogPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/blog/*" element={<LazyRoute><BlogRedirect /></LazyRoute>} />
              <Route path="/videos" element={<WithBBQLayout><LazyRoute><BBQVideosPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/videos/:slug" element={<WithBBQLayout><LazyRoute><BBQVideoDetailPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/privacy" element={<WithBBQLayout><LazyRoute><BBQPrivacyPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/terms" element={<WithBBQLayout><LazyRoute><BBQTermsPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/trade" element={<WithBBQLayout><LazyRoute><BBQTradePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/commercial" element={<WithBBQLayout><LazyRoute><BBQCommercialPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/install" element={<WithBBQLayout><LazyRoute><BBQInstallPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/agreement/:token" element={<LazyRoute><CustomerContractPage /></LazyRoute>} />
              
              {/* Category Routes */}
              <Route path="/grills" element={<WithBBQLayout><LazyRoute><BBQCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/grills/:subcategory" element={<WithBBQLayout><LazyRoute><BBQSubcategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/smokers" element={<WithBBQLayout><LazyRoute><BBQCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/smokers/:subcategory" element={<WithBBQLayout><LazyRoute><BBQSubcategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchens" element={<WithBBQLayout><LazyRoute><BBQCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchens/:subcategory" element={<WithBBQLayout><LazyRoute><BBQSubcategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/design-wizard" element={<LazyRoute><BBQDesignWizardPage /></LazyRoute>} />
              <Route path="/pizza-ovens" element={<WithBBQLayout><LazyRoute><BBQCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/pizza-ovens/karma" element={<WithBBQLayout><LazyRoute><BBQKarmaPizzaOvensPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/pizza-ovens/buying-guide" element={<WithBBQLayout><LazyRoute><BBQPizzaOvenGuidePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/pizza-ovens/accessories" element={<WithBBQLayout><LazyRoute><BBQPizzaAccessoriesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/pizza-ovens/recipes" element={<WithBBQLayout><LazyRoute><BBQPizzaRecipesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/pizza-ovens/:subcategory" element={<WithBBQLayout><LazyRoute><BBQSubcategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/refrigeration" element={<WithBBQLayout><LazyRoute><BBQCategoryPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/refrigeration/:subcategory" element={<WithBBQLayout><LazyRoute><BBQSubcategoryPage /></LazyRoute></WithBBQLayout>} />
              
              {/* SEO Routes - Brand Hub, Brands, Comparisons, Best-For, Location Hub, City Pages */}
              <Route path="/outdoor-kitchen-brands" element={<WithBBQLayout><LazyRoute><BBQBrandHubPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchens-dallas" element={<WithBBQLayout><LazyRoute><BBQLocationHubPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/locations" element={<WithBBQLayout><LazyRoute><BBQLocationsPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/locations/:citySlug" element={<WithBBQLayout><LazyRoute><BBQCityPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchens-:citySlug" element={<WithBBQLayout><LazyRoute><BBQOutdoorKitchenCityPage /></LazyRoute></WithBBQLayout>} />
              
              {/* Multi-City SEO Routes - /tx/{city}/ structure */}
              <Route path="/service-areas" element={<WithBBQLayout><LazyRoute><BBQServiceAreasPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/tx/schedule" element={<WithBBQLayout><LazyRoute><BBQSchedulePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/tx/showroom" element={<WithBBQLayout><LazyRoute><BBQShowroomPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/tx/:citySlug" element={<WithBBQLayout><LazyRoute><BBQCityLandingPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/tx/:citySlug/:neighborhoodSlug" element={<WithBBQLayout><LazyRoute><BBQNeighborhoodPage /></LazyRoute></WithBBQLayout>} />
              
              {/* California Market Routes */}
              <Route path="/showrooms/inland-empire" element={<WithBBQLayout><LazyRoute><CAShowroomPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/ca/showroom" element={<WithBBQLayout><LazyRoute><CAShowroomPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/ca/inland-empire" element={<WithBBQLayout><LazyRoute><CAInlandEmpirePage /></LazyRoute></WithBBQLayout>} />
              <Route path="/ca/los-angeles" element={<WithBBQLayout><LazyRoute><CALosAngelesPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/ca/schedule" element={<WithBBQLayout><LazyRoute><CASchedulePage /></LazyRoute></WithBBQLayout>} />
              
              {/* PPC Landing Pages - noindex, conversion-focused */}
              <Route path="/ppc/tx" element={<LazyRoute><PPCTexasHubPage /></LazyRoute>} />
              <Route path="/ppc/tx/:citySlug" element={<LazyRoute><PPCCityPage /></LazyRoute>} />
              <Route path="/ppc/ca" element={<LazyRoute><PPCCaliforniaHubPage /></LazyRoute>} />
              <Route path="/ppc/ca/:citySlug" element={<LazyRoute><PPCCACityPage /></LazyRoute>} />
              
              <Route path="/outdoor-kitchen/best/:bestForSlug" element={<WithBBQLayout><LazyRoute><BBQBestForPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchen/compare/:comparisonSlug" element={<WithBBQLayout><LazyRoute><BBQComparisonPage /></LazyRoute></WithBBQLayout>} />
              <Route path="/outdoor-kitchen/:brandSlug" element={<WithBBQLayout><LazyRoute><BBQBrandPage /></LazyRoute></WithBBQLayout>} />
              
              {/* Legacy redirects for old /bbq/* paths */}
              <Route path="/bbq/order-tracker" element={<Navigate to="/order-tracker" replace />} />
              <Route path="/bbq/showroom" element={<Navigate to="/showroom" replace />} />
              <Route path="/bbq/quote" element={<Navigate to="/quote" replace />} />
              <Route path="/bbq/schedule" element={<Navigate to="/schedule" replace />} />
              <Route path="/bbq/financing" element={<Navigate to="/financing" replace />} />
              <Route path="/bbq/delivery" element={<Navigate to="/delivery" replace />} />
              <Route path="/bbq/warranty" element={<Navigate to="/warranty" replace />} />
              <Route path="/bbq/design-services" element={<Navigate to="/design-services" replace />} />
              <Route path="/bbq/clearance" element={<Navigate to="/clearance" replace />} />
              <Route path="/bbq/accessories" element={<Navigate to="/accessories" replace />} />
              <Route path="/bbq/guides" element={<Navigate to="/guides" replace />} />
              <Route path="/bbq/buying-guide" element={<Navigate to="/buying-guide" replace />} />
              <Route path="/bbq/privacy" element={<Navigate to="/privacy" replace />} />
              <Route path="/bbq/terms" element={<Navigate to="/terms" replace />} />
              <Route path="/bbq/trade" element={<Navigate to="/trade" replace />} />
              <Route path="/bbq/commercial" element={<Navigate to="/commercial" replace />} />
              <Route path="/bbq/grills" element={<Navigate to="/grills" replace />} />
              <Route path="/bbq/smokers" element={<Navigate to="/smokers" replace />} />
              <Route path="/bbq/outdoor-kitchens" element={<Navigate to="/outdoor-kitchens" replace />} />
              <Route path="/bbq/pizza-ovens" element={<Navigate to="/pizza-ovens" replace />} />
              <Route path="/bbq/refrigeration" element={<Navigate to="/refrigeration" replace />} />
              <Route path="/bbq/outdoor-kitchen-brands" element={<Navigate to="/outdoor-kitchen-brands" replace />} />
              <Route path="/bbq/outdoor-kitchens-dallas" element={<Navigate to="/outdoor-kitchens-dallas" replace />} />
              <Route path="/bbq/locations" element={<Navigate to="/locations" replace />} />
              
              {/* Catch-all */}
              <Route path="*" element={<WithBBQLayout><LazyRoute><NotFound /></LazyRoute></WithBBQLayout>} />
            </Routes>
            <Suspense fallback={null}>
              <QuoteButtonLinter />
            </Suspense>
          </KioskProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
